/**
 * System detail hook using React Relay.
 *
 * Wraps getSystem(id) and derives the legacy `system` shape + `modeInfo`
 * (via buildSystemInfo) used by the hardware detail page in both system and
 * organization views.
 *
 * Note: Run `npm run relay` after editing the GraphQL query.
 */
import { fetchQuery } from 'react-relay';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useSystemRelayQuery as UseSystemRelayQuery } from './__generated__/useSystemRelayQuery.graphql';
import { buildSystemInfo, RawSystem } from './systemInfoMapper';

const SystemQuery = graphql`
  query useSystemRelayQuery($id: ID!) {
    getSystem(id: $id) {
      id
      oldId
      name
      newHardware
      rentableHardwareType {
        id
        name
        namePlural
      }
    }
  }
`;

export function useSystemRelay(
  systemId: string | undefined,
  options?: { enabled?: boolean }
) {
  const [raw, setRaw] = useState<RawSystem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [fetchCount, setFetchCount] = useState(0);
  const [error, setError] = useState<Error | null>(null);
  const isFetchedRef = useRef(isFetched);

  const enabled = options?.enabled !== false && Boolean(systemId);

  useEffect(() => {
    isFetchedRef.current = isFetched;
  }, [isFetched]);

  const doFetch = useCallback(async () => {
    if (!systemId) return;
    try {
      if (!isFetchedRef.current) setIsLoading(true);
      const result = await fetchQuery<UseSystemRelayQuery>(
        relayEnvironment,
        SystemQuery,
        { id: systemId }
      ).toPromise();
      if (result) {
        setRaw((result.getSystem as RawSystem) ?? null);
        setError(null);
      }
    } catch (err) {
      // Leave raw null; buildSystemInfo yields a safe default modeInfo.
      const errorObj = err instanceof Error ? err : new Error('Failed to fetch system');
      setError(errorObj);
      console.error('[useSystemRelay] fetch error:', errorObj);
    } finally {
      setIsLoading(false);
      setIsFetched(true);
    }
  }, [systemId]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    doFetch();
  }, [enabled, systemId, fetchCount, doFetch]);

  const refetch = useCallback(() => setFetchCount((c) => c + 1), []);

  const { system, modeInfo } = useMemo(() => buildSystemInfo(raw), [raw]);

  return useMemo(
    () => ({ system, modeInfo, isLoading, error, refetch }),
    [system, modeInfo, isLoading, error, refetch]
  );
}
